import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faPlayCircle,
} from "@fortawesome/free-solid-svg-icons";
import { SupportVideo } from "../../../services/support-service";
import { BASE_API_URL } from "../../../config/api";

const extractYouTubeId = (url: string): string | null => {
  const regex =
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

const buildStreamUrl = (url: string): string => {
  if (!url || extractYouTubeId(url)) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    const pathname = decodeURIComponent(parsed.pathname.replace(/^\/+/, ""));
    return `${BASE_API_URL}local-storage/stream-video?fileName=${encodeURIComponent(pathname)}`;
  } catch {
    const sanitized = url.replace(/^\/+/, "");
    return `${BASE_API_URL}local-storage/stream-video?fileName=${encodeURIComponent(sanitized)}`;
  }
};

const toRawList = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  if (Array.isArray(payload.data)) return payload.data;
  if (payload.data && typeof payload.data === "object" && Array.isArray(payload.data.data)) {
    return payload.data.data;
  }
  return [];
};

const normalizeVideoType = (video: any): string =>
  String(video?.videoType ?? video?.type ?? "").trim().toUpperCase();

const toVideoList = (payload: any): SupportVideo[] => {
  return toRawList(payload)
    .map((video: any) => ({
      ...video,
      id: Number(video?.id ?? 0),
      title: String(video?.title ?? ""),
      youtubeUrl: String(video?.youtubeUrl ?? video?.youtube_url ?? ""),
      videoType: (normalizeVideoType(video) || "SUPPORT") as SupportVideo["videoType"],
    }))
    .filter((video) => Boolean(video.youtubeUrl));
};

const SuccessCarousel = () => {
  const [videos, setVideos] = useState<SupportVideo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [index, setIndex] = useState<number>(0);

  useEffect(() => {
    let isActive = true;
    const controller = new AbortController();

    const loadTestimonials = async () => {
      try {
        const response = await fetch(
          `${BASE_API_URL}support-videos`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
            cache: "no-store",
            signal: controller.signal,
          },
        );
        let list: SupportVideo[] = [];
        if (response.ok) {
          const payload = await response.json();
          const allVideos = toVideoList(payload);
          list = allVideos.filter((video) => {
            const normalizedType = normalizeVideoType(video);
            const isUploadedFileVideo = !extractYouTubeId(video.youtubeUrl);
            return normalizedType === "TESTIMONIAL" || isUploadedFileVideo;
          });
        }

        if (isActive) {
          setVideos(list);
        }
      } catch (error) {
        if (isActive) {
          setVideos([]);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadTestimonials();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    setIndex(0);
  }, [videos.length]);

  const activeVideo = useMemo(() => {
    if (videos.length === 0) return null;
    return videos[index];
  }, [videos, index]);

  const activeYouTubeId = activeVideo ? extractYouTubeId(activeVideo.youtubeUrl) : null;
  const activeFileVideoUrl = activeVideo && !activeYouTubeId ? buildStreamUrl(activeVideo.youtubeUrl) : null;

  const next = () => {
    if (videos.length <= 1) return;
    setIndex((i) => (i + 1) % videos.length);
  };

  const prev = () => {
    if (videos.length <= 1) return;
    setIndex((i) => (i - 1 + videos.length) % videos.length);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-black text-title uppercase tracking-tighter">
            Nos <span className="text-primary italic">Reussites</span>
          </h2>
          <p className="text-sm text-text/40 uppercase tracking-widest mt-2">
            Decouvrez les temoignages video de nos etudiants
          </p>
        </div>

        <div className="relative group">
          <div className="overflow-hidden rounded-[3rem] shadow-2xl bg-gray-50 border border-gray-100 flex items-center justify-center">
            {loading ? (
              <div className="w-full aspect-video bg-slate-100 animate-pulse" />
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeVideo?.id ?? "empty"}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                  className="w-full aspect-video flex items-center justify-center bg-black"
                >
                  {activeVideo && activeYouTubeId ? (
                    <iframe
                      title={activeVideo.title}
                      src={`https://www.youtube.com/embed/${activeYouTubeId}?rel=0&modestbranding=1`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full border-0"
                    />
                  ) : activeVideo && activeFileVideoUrl ? (
                    <video
                      className="w-full h-full"
                      controls
                      preload="metadata"
                      src={activeFileVideoUrl}
                    />
                  ) : (
                    <div className="w-full h-full bg-slate-100 flex flex-col items-center justify-center gap-4 group/video">
                      <FontAwesomeIcon
                        icon={faPlayCircle}
                        className="text-6xl text-primary/20 group-hover/video:text-primary transition-colors duration-500"
                      />
                      <p className="text-[11px] font-black text-text/20 uppercase tracking-[0.4em] text-center px-4">
                        {activeVideo ? activeVideo.title : "Aucun temoignage disponible"}
                      </p>
                      {!activeVideo && (
                        <span className="px-4 py-2 bg-white rounded-full text-[9px] font-bold text-text/40 shadow-sm">
                          Ajoutez des videos temoignages depuis le dashboard admin
                        </span>
                      )}
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            )}
          </div>

          {videos.length > 1 && (
            <>
              <div className="absolute top-1/2 -translate-y-1/2 left-4 md:-left-10">
                <button
                  onClick={prev}
                  className="w-12 h-12 md:w-16 md:h-16 rounded-3xl bg-white border border-gray-100 shadow-xl flex items-center justify-center text-title hover:bg-primary hover:text-white transition-all duration-300"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
              </div>
              <div className="absolute top-1/2 -translate-y-1/2 right-4 md:-right-10">
                <button
                  onClick={next}
                  className="w-12 h-12 md:w-16 md:h-16 rounded-3xl bg-white border border-gray-100 shadow-xl flex items-center justify-center text-title hover:bg-primary hover:text-white transition-all duration-300"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </>
          )}

          {videos.length > 1 && (
            <div className="flex justify-center mt-10 gap-2">
              {videos.map((video, i) => (
                <button
                  key={video.id}
                  onClick={() => setIndex(i)}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    index === i ? "w-8 bg-primary shadow-lg shadow-primary/30" : "w-2 bg-slate-200"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default SuccessCarousel;
